import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import type { ElectronContext } from '@mimir/common/context/applicationContext';
import { execFilePromise, execPromise } from './execUtils';

/**
 * Tries to get the PID of the running process on Windows and Mac.
 * Multiple processes with same name running at the same time (e.g. multiple AME versions)
 * is not handled here - only one PID is returned.
 */
const getPid = async (electronContext: ElectronContext, processName: string) => {
  try {
    let processListOutput: string;
    let regex: RegExp;
    if (electronContext.isWindows) {
      processListOutput = await execFilePromise(electronContext, 'TASKLIST', ['/nh', '/fi', `IMAGENAME eq ${processName}.exe`]);
      /**
       * Sample output:
       *
       * Adobe Media Encoder.exe       8812 Console                    1    818,812 K
       *
       */
      regex = new RegExp(`${processName}.exe\\s+([0-9]+)\\s+.*`);
    } else {
      processListOutput = await execPromise(electronContext, `pgrep "${processName}"`);
      regex = /\s*([0-9]+).*/;
    }
    const result = processListOutput.match(regex);
    if (result && result.length > 0) {
      return parseInt(result[1]);
    }
  } catch (err) {
    // suppress
  }
};

// Check for already running process, send it signal to terminate, and wait 30s
// to make sure it is gone, before returning from this function.
export const strictlyTerminateProcess = async (electronContext: ElectronContext, processName: string) => {
  const pid = await getPid(electronContext, processName);

  if (!pid) {
    return;
  }

  console.debug(`Terminating already running ${processName} process ${pid}`);
  const processTerminationError = terminateProcess(electronContext, pid);
  if (processTerminationError) {
    if (!processTerminationError.message?.includes('kill ESRCH')) {
      throw processTerminationError;
    }

    // We got ESRCH which means process is not found e.g. already terminated. Proceed normally.
  }

  // Check for at most 30s if process is really dead
  let processIsDead = false;
  for (let tries = 0; !processIsDead && tries < 30; tries++) {
    await asyncTimeout(1000);
    processIsDead = await getPid(electronContext, processName) === undefined;
  }

  // No use proceeding further if we cannot get rid of the currently running process
  if (!processIsDead) {
    throw new Error(`Existing ${processName} process ${pid} could not be terminated`);
  }

  console.debug(`Done terminating already running ${processName} process ${pid}`);
};

// Returns nothing (if successful), or error (if thrown from the kill API)
export const terminateProcess = (electronContext: ElectronContext, pid: number) => {
  try {
    electronContext.mainRequire('process').kill(pid, 'SIGKILL');
  } catch (err: any) {
    return err;
  }
};
